/**
 * React Query hooks for MCP server CRUD operations.
 *
 * Follows the same pattern as useAgents.ts.
 * Uses tenant-aware `api` helper from @/lib/api.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  MCPCredentialMetadata,
  MCPServerCreate,
  MCPServerCredentials,
  MCPServerListResponse,
  MCPServerResponse,
  MCPServerTestResult,
  MCPServerToolsResponse,
  MCPServerUpdate,
} from '../types';

const MCP_SERVERS_KEY = 'mcp-servers';
const MCP_TOOLS_KEY = 'mcp-server-tools';
const MCP_CREDENTIALS_KEY = 'mcp-server-credentials';

// =============================================================================
// Query Hooks
// =============================================================================

interface UseServersOptions {
  page?: number;
  pageSize?: number;
  status?: string;
  executionTier?: string;
  search?: string;
}

export function useMCPServers(options: UseServersOptions = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const { page = 1, pageSize = 20, status, executionTier, search } = options;

  return useQuery({
    queryKey: [MCP_SERVERS_KEY, tenantId, page, pageSize, status, executionTier, search],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      if (status) params.set('status', status);
      if (executionTier) params.set('execution_tier', executionTier);
      if (search) params.set('search', search);

      const { data } = await apiClient.get<MCPServerListResponse>(`/api/v1/mcp/servers?${params.toString()}`);
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useMCPServer(serverId: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [MCP_SERVERS_KEY, tenantId, serverId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<MCPServerResponse>(`/api/v1/mcp/servers/${serverId}`);
      return data;
    },
    enabled: !!serverId && !!tenantId,
  });
}

export function useMCPServerTools(serverId: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [MCP_TOOLS_KEY, tenantId, serverId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<MCPServerToolsResponse>(`/api/v1/mcp/servers/${serverId}/tools`);
      return data;
    },
    enabled: !!serverId && !!tenantId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateMCPServer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serverData: MCPServerCreate) => {
      const { data } = await apiClient.post<MCPServerResponse>('/api/v1/mcp/servers', serverData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_KEY] });
    },
  });
}

export function useUpdateMCPServer(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serverData: MCPServerUpdate) => {
      const { data } = await apiClient.patch<MCPServerResponse>(`/api/v1/mcp/servers/${serverId}`, serverData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_KEY] });
    },
  });
}

export function useDeleteMCPServer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serverId: string) => {
      await apiClient.delete(`/api/v1/mcp/servers/${serverId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_KEY] });
    },
  });
}

export function useUpdateMCPServerCredentials(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: MCPServerCredentials) => {
      await apiClient.put(`/api/v1/mcp/servers/${serverId}/credentials`, credentials);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_KEY, undefined, serverId] });
    },
  });
}

export function useDiscoverMCPTools(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<MCPServerToolsResponse>(`/api/v1/mcp/servers/${serverId}/tools/discover`, {});
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_TOOLS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_KEY] });
    },
  });
}

export function useTestMCPConnection(serverId: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<MCPServerTestResult>(`/api/v1/mcp/servers/${serverId}/test`, {});
      return data;
    },
  });
}

// =============================================================================
// Credential Hooks
// =============================================================================

export function useCredentialMetadata(serverId: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [MCP_CREDENTIALS_KEY, tenantId, serverId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<MCPCredentialMetadata>(`/api/v1/mcp/servers/${serverId}/credentials`);
      return data;
    },
    enabled: !!serverId && !!tenantId,
  });
}

export function useDeleteCredentials(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/api/v1/mcp/servers/${serverId}/credentials`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MCP_CREDENTIALS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MCP_SERVERS_KEY] });
    },
  });
}
